'use client';

import { useEffect, useState } from 'react';

export interface MediumPost {
  title: string;
  pubDate: string;
  link: string;
  content: string;
  description: string;
  thumbnail?: string;
  author?: string;
}

const MEDIUM_RSS_URL = 'https://medium.com/feed/@LFG_Inc';

function extractThumbnailFromItem(item: any): string {
  let thumbnail = '';
  const descImgMatch = item.description?.match(/<img[^>]+src=\"([^\">]+)\"/);
  if (descImgMatch?.[1]) thumbnail = descImgMatch[1];
  if (!thumbnail && item.content) {
    const contentImgMatch = item.content.match(/<img[^>]+src=\"([^\">]+)\"/);
    if (contentImgMatch?.[1]) thumbnail = contentImgMatch[1];
  }
  return thumbnail;
}

function extractExcerptFromItem(item: any, maxLength = 160): string {
  const tempDiv = typeof document !== 'undefined' ? document.createElement('div') : null;
  if (!tempDiv) return '';
  tempDiv.innerHTML = item.description || '';
  const plainText = tempDiv.textContent || tempDiv.innerText || '';
  return plainText.slice(0, maxLength) + (plainText.length > maxLength ? '...' : '');
}

export const useMediumNews = () => {
  const [posts, setPosts] = useState<MediumPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchPosts = async () => {
      try {
        const url = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(MEDIUM_RSS_URL)}`;
        const res = await fetch(url);
        const data = await res.json();

        if (!cancelled && data.status === 'ok' && data.items) {
          const mapped: MediumPost[] = data.items.map((item: any) => ({
            title: item.title,
            pubDate: item.pubDate,
            link: item.link,
            content: item.content || item.description || '',
            description: extractExcerptFromItem(item),
            thumbnail: extractThumbnailFromItem(item),
            author: item.author,
          }));
          setPosts(mapped);
        }
      } catch (err) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('Error fetching Medium posts:', err);
          setError('Failed to load news');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchPosts();

    return () => {
      cancelled = true;
    };
  }, []);

  return { posts, loading, error };
};
